import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import { adminAPI } from '../../utils/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

const ASSET_NETWORKS = {
  BTC: 'testnet3',
  ETH: 'sepolia',
  USDT: 'sepolia',
  USDC: 'sepolia',
  BNB: 'bsc-testnet',
  SOL: 'devnet',
  XRP: 'xrp-testnet',
  ADA: 'preprod',
  DOGE: 'testnet',
  LTC: 'testnet',
  MATIC: 'amoy',
};

const SUPPORTED_ASSETS = [
  { symbol: 'BTC',  name: 'Bitcoin'    },
  { symbol: 'ETH',  name: 'Ethereum'   },
  { symbol: 'USDT', name: 'Tether USD' },
  { symbol: 'USDC', name: 'USD Coin'   },
  { symbol: 'BNB',  name: 'BNB'        },
  { symbol: 'XRP',  name: 'XRP'        },
  { symbol: 'SOL',  name: 'Solana'     },
  { symbol: 'ADA',  name: 'Cardano'    },
  { symbol: 'DOGE', name: 'Dogecoin'   },
  { symbol: 'LTC',  name: 'Litecoin'   },
  { symbol: 'MATIC', name: 'Polygon'   },
];

const AdminUserWallets = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSymbol, setEditingSymbol] = useState(null);
  const [editAddress, setEditAddress] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [userRes, walletsRes] = await Promise.all([
        adminAPI.getUser(userId),
        adminAPI.getUserWallets(userId),
      ]);
      setUser(userRes.data.user);
      setWallets(walletsRes.data.wallets || []);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const walletBySymbol = Object.fromEntries(wallets.map((w) => [w.asset_symbol, w]));

  const startEdit = (asset) => {
    const existing = walletBySymbol[asset.symbol];
    setEditingSymbol(asset.symbol);
    setEditAddress(existing?.wallet_address || '');
  };

  const cancelEdit = () => {
    setEditingSymbol(null);
    setEditAddress('');
  };

  const saveWallet = async (asset) => {
    if (!editAddress.trim()) {
      toast.error('Wallet address is required');
      return;
    }
    setSaving(true);
    try {
      await adminAPI.assignWallet(userId, {
        asset_symbol: asset.symbol,
        wallet_address: editAddress.trim(),
        network: ASSET_NETWORKS[asset.symbol],
      });
      toast.success(`${asset.symbol} wallet assigned`);
      setEditingSymbol(null);
      setEditAddress('');
      adminAPI.getUserWallets(userId).then((res) => setWallets(res.data.wallets || []));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign wallet');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <button
        onClick={() => navigate('/admin/users')}
        className="flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-sm mb-6 transition-colors"
      >
        <ArrowLeft size={16} />
        Back to Users
      </button>

      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
          Wallet Assignment
          {user && (
            <span className="text-muted-foreground font-normal">
              {' — '}{user.full_name} ({user.email})
            </span>
          )}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Assign blockchain wallet addresses for this user
        </p>
      </div>

      <Card className="glass-card p-0 overflow-hidden">
        <div className="admin-table-wrapper">
          <table>
            <thead>
              <tr className="border-b border-border">
                <th className="text-left text-muted-foreground text-sm font-medium p-4">Asset</th>
                <th className="text-left text-muted-foreground text-sm font-medium p-4">Network</th>
                <th className="text-left text-muted-foreground text-sm font-medium p-4">Wallet Address</th>
                <th className="text-right text-muted-foreground text-sm font-medium p-4">Balance</th>
                <th className="text-right text-muted-foreground text-sm font-medium p-4">Available</th>
                <th className="text-center text-muted-foreground text-sm font-medium p-4">Action</th>
              </tr>
            </thead>
            <tbody>
              {SUPPORTED_ASSETS.map((asset) => {
                const w = walletBySymbol[asset.symbol];
                const isEditing = editingSymbol === asset.symbol;
                const hasAddress = w?.wallet_address;
                const network = ASSET_NETWORKS[asset.symbol];

                return (
                  <React.Fragment key={asset.symbol}>
                    <tr className="table-row border-b border-border last:border-0">
                      <td className="p-4">
                        <div className="font-semibold text-foreground">{asset.symbol}</div>
                        <div className="text-xs text-muted-foreground">{asset.name}</div>
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-muted-foreground font-mono">{network}</span>
                      </td>
                      <td className="p-4 max-w-xs">
                        {hasAddress ? (
                          <span className="text-sm font-mono text-foreground break-all">
                            {w.wallet_address}
                          </span>
                        ) : (
                          <span className="text-sm font-medium text-red-500">Not assigned</span>
                        )}
                      </td>
                      <td className="p-4 text-right text-sm text-foreground">
                        {w ? w.balance.toFixed(8) : '—'}
                      </td>
                      <td className="p-4 text-right text-sm text-foreground">
                        {w ? w.available_balance.toFixed(8) : '—'}
                      </td>
                      <td className="p-4 text-center">
                        {!isEditing && (
                          <Button
                            size="sm"
                            variant={hasAddress ? 'outline' : 'default'}
                            onClick={() => startEdit(asset)}
                          >
                            {hasAddress ? 'Edit' : 'Assign'}
                          </Button>
                        )}
                        {isEditing && (
                          <Button size="sm" variant="ghost" onClick={cancelEdit} disabled={saving}>
                            Cancel
                          </Button>
                        )}
                      </td>
                    </tr>
                    {isEditing && (
                      <tr className="bg-muted/30 border-b border-border">
                        <td colSpan={6} className="px-4 py-3">
                          <div className="flex items-end gap-3 flex-wrap">
                            <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
                              <label className="text-xs text-muted-foreground font-medium">
                                {asset.symbol} Wallet Address
                              </label>
                              <input
                                className="input-field text-sm font-mono"
                                value={editAddress}
                                onChange={(e) => setEditAddress(e.target.value)}
                                placeholder={`Enter ${asset.symbol} wallet address...`}
                                autoFocus
                              />
                            </div>
                            <div className="flex flex-col gap-1">
                              <label className="text-xs text-muted-foreground font-medium">Network</label>
                              <div className="px-3 py-2 text-sm font-mono text-muted-foreground bg-muted rounded-md border border-border">
                                {network}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" onClick={() => saveWallet(asset)} disabled={saving}>
                                {saving ? 'Saving…' : 'Save'}
                              </Button>
                              <Button size="sm" variant="outline" onClick={cancelEdit} disabled={saving}>
                                Cancel
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default AdminUserWallets;
